import { initializeApp, getApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

function getConfig(): FirebaseConfig {
  const env = typeof import.meta !== 'undefined' ? (import.meta as any).env ?? {} : {};
  return {
    apiKey: env.VITE_FIREBASE_API_KEY || '',
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || `${env.VITE_FIREBASE_PROJECT_ID || ''}.firebaseapp.com`,
    projectId: env.VITE_FIREBASE_PROJECT_ID || '',
  };
}

export function getFirebaseApp(): FirebaseApp {
  if (app) return app;
  if (getApps().length > 0) {
    app = getApp();
    return app;
  }
  app = initializeApp(getConfig());
  return app;
}

export function getFirebaseAuth(): Auth {
  if (auth) return auth;
  auth = getAuth(getFirebaseApp());

  const env = typeof import.meta !== 'undefined' ? (import.meta as any).env ?? {} : {};
  const emulatorHost = env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
  if (emulatorHost) {
    connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true });
  }

  return auth;
}

// Cache the ID token so we don't call getIdToken() on every request.
let cachedToken: string | null = null;
let cachedUid: string | null = null;
let tokenExpiry = 0;

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const a = getFirebaseAuth();
  const user = a.currentUser;
  if (!user) return null;

  const now = Date.now();
  if (!forceRefresh && cachedToken && cachedUid === user.uid && tokenExpiry > now) {
    return cachedToken;
  }

  cachedToken = await user.getIdToken(forceRefresh);
  cachedUid = user.uid;
  // Firebase tokens expire in 1 hour; cache for 55 minutes.
  tokenExpiry = now + 55 * 60 * 1000;
  return cachedToken;
}

export async function signIn(email: string, password: string) {
  const a = getFirebaseAuth();
  const cred = await signInWithEmailAndPassword(a, email, password);
  cachedToken = null;
  return cred;
}

export async function signUp(email: string, password: string) {
  const a = getFirebaseAuth();
  const cred = await createUserWithEmailAndPassword(a, email, password);
  cachedToken = null;
  return cred;
}

export async function signInWithGoogle() {
  const a = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(a, provider);
  cachedToken = null;
  return cred;
}

export async function signOut() {
  const a = getFirebaseAuth();
  cachedToken = null;
  cachedUid = null;
  tokenExpiry = 0;
  await firebaseSignOut(a);
}

export async function resetPassword(email: string) {
  const a = getFirebaseAuth();
  await sendPasswordResetEmail(a, email);
}

export async function updatePassword(newPassword: string) {
  const a = getFirebaseAuth();
  const user = a.currentUser;
  if (!user) throw new Error('Not authenticated');
  await firebaseUpdatePassword(user, newPassword);
}

export function onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
  const a = getFirebaseAuth();
  return firebaseOnAuthStateChanged(a, (user) => {
    if (!user) {
      cachedToken = null;
      cachedUid = null;
      tokenExpiry = 0;
    }
    callback(user);
  });
}

export type { FirebaseUser };
